import { IVectorLayerGroupConfig } from "@esdm/map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource, WfsBboxAsWktRequestParams } from "@esdm/map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "@esdm/map/dist/layers/vector/VectorLayerStrategy";
import { PortsmouthRecordType, PortsmouthRecordSubType } from "./PortsmouthRecordTypes";
import { IWmsLayerGroupConfig } from "@esdm/map/dist/layers/groups/wms/interfaces/IWmsLayerGroupConfig";
import { FeatureAdd } from "@esdm/map/dist/layers/vector/sources/loaders";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/OgcService/hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(),{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: PortsmouthRecordType,
        recordSubType: PortsmouthRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig | IWmsLayerGroupConfig = null;

        switch (recordType) {
            case PortsmouthRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;         

            case PortsmouthRecordType.Event:
                layerConfig = this.Event(opts);
                break;     

            case PortsmouthRecordType.AreaBoundary:
                layerConfig = this.AreaBoundary();
                break;
        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case PortsmouthRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;  

                case PortsmouthRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case PortsmouthRecordSubType.RegisteredParksAndGardens:
                    layerConfig = this.RegisteredParksAndGardens(opts);
                    break; 
            }
        }

        return layerConfig;
    }

    private AreaBoundary = (): IWmsLayerGroupConfig => {
        return {
            displayName: "Portsmouth Boundary",
            ref: "AreaBoundary",
            type: PortsmouthRecordType.AreaBoundary,
            url: this.mapFileUrl,
            displayInLayerSwitcher: false,
            displayInLegend: false,
            visibleOnLoad: true,
            queryable: false,
            layers: [
                {
                    layerName: "AreaBoundary",
                    minZoom: 1,
                    maxZoom: 19
                }
            ]
        }
    }

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: PortsmouthRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                //legend: { line: false, polygon: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }                    
                ]
            }, ...opts
        };
    }

    private Event = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Events",
                ref: "Events",
                type: PortsmouthRecordType.Event,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                //legend: { line: false, polygon: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "EventPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: PortsmouthRecordType.Designation,
                subType: PortsmouthRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: PortsmouthRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: PortsmouthRecordType.Designation,
                subType: PortsmouthRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: PortsmouthRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
 
    private RegisteredParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Parks and Gardens",
                ref: "RegisteredParksAndGardens",
                type: PortsmouthRecordType.Designation,
                subType: PortsmouthRecordSubType.RegisteredParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: PortsmouthRecordSubType.RegisteredParksAndGardens },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
}

